import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { FeatureFlagService } from '../services/feature-flag.service';
import { TenantSettingsService } from '../services/tenant-settings.service';

/**
 * Blocks a route when its environment-level feature flag is off (e.g. the demo
 * sandbox). The sidebar already locks these nav entries; this closes the
 * direct-URL gap so a typed URL can't reach a gated page (which would 403).
 *
 * Usage:
 *   canActivate: [moduleAccessGuard, featureFlagGuard],
 *   data: { modules: [...], featureFlag: 'saasLogicBilling' }
 */
export type FeatureFlagKey =
  | 'saasLogicBilling'
  | 'sso'
  | 'paidEventRegistration'
  | 'customDomain'
  | 'storefront';

export const featureFlagGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const features = inject(FeatureFlagService);
  const tenantSettings = inject(TenantSettingsService);
  const router = inject(Router);

  // Flags ride on the settings payload — make sure they're loaded before deciding,
  // otherwise a deep-link could evaluate against the default (enabled) state.
  if (!tenantSettings.featureFlags()) {
    await tenantSettings.loadSettings();
  }

  const flag = route.data['featureFlag'] as FeatureFlagKey | undefined;
  const enabled: Record<FeatureFlagKey, boolean> = {
    saasLogicBilling: features.saasLogicEnabled(),
    sso: features.ssoEnabled(),
    paidEventRegistration: features.paidEventsEnabled(),
    customDomain: features.customDomainEnabled(),
    storefront: features.storefrontEnabled(),
  };

  if (flag && !enabled[flag]) {
    // Gated off — send them back to the dashboard.
    return router.createUrlTree(['/']);
  }
  return true;
};
